import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styles } from "./stylesSwapModal";
import { useSwapMutation } from "@/store/api/walletApi";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

type Asset = "BTC" | "SATS" | "USDT";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { from: Asset; to: Asset; amount?: number }) => void;
  initialFrom?: Asset;
  initialTo?: Asset;
};

const ASSETS: Asset[] = ["BTC", "SATS", "USDT"];

const assetLabel = (asset: Asset) => (asset === "USDT" ? "USDC" : asset);

const SwapIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...props} aria-hidden>
    <path
      d="M7 7h11l-3-3 1.4-1.4L22.8 7l-6.4 4.4L15 10l3-2H7V7zm10 10H6l3 3-1.4 1.4L1.2 17l6.4-4.4L9 14l-3 2h11v1z"
      fill="currentColor"
    />
  </svg>
);

export default function SwapModal({ open, onClose, onConfirm, initialFrom, initialTo }: Props) {
  const [from, setFrom] = useState<Asset | null>(null);
  const [to, setTo] = useState<Asset | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [swap, { isLoading: swapping, error: swapError, reset: resetSwap }] = useSwapMutation();
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);

  const handleClose = () => {
    setFrom(null);
    setTo(null);
    setAmount("");
    resetSwap();
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    setFrom(null);
    setTo(null);
    setAmount("");
    resetSwap();

    if (initialFrom && initialTo && initialFrom !== initialTo) {
      setFrom(initialFrom);
      setTo(initialTo);
    }
  }, [open, initialFrom, initialTo, resetSwap]);

  const numericAmount = useMemo(() => {
    if (!amount) return null;
    const n = Number(amount.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }, [amount]);

  const confirmDisabled = useMemo(() => {
    if (!from || !to) return true;
    if (from === to) return true;
    if (!numericAmount || numericAmount <= 0) return true;
    return false;
  }, [from, to, numericAmount]);

  const swapSides = () => {
    if (!from && !to) return;
    setFrom(to);
    setTo(from);
  };

  const handleConfirm = useCallback(async () => {
    if (!from || !to || !numericAmount) return;
    try {
      await swap({
        amount: numericAmount.toString(),
        currency: "USD",
        swap_out: from,
        swap_in: to,
      }).unwrap();

      onConfirm({ from, to, amount: numericAmount });
      handleClose();
    } catch (err) {
      console.error("Swap failed:", err);

      let message = "Failed to swap. Please try again.";

      const error = err as FetchBaseQueryError & { data?: { message?: string } };
      if (error?.data && typeof error.data === "object" && "message" in error.data) {
        message = (error.data as any).message ?? message;
      }

      setToast({ message, success: false });
    }
  }, [from, to, numericAmount, swap, onConfirm, handleClose]);

  const handlePick = (side: "from" | "to", asset: Asset) => {
    const current = side === "from" ? from : to;
    const opposite = side === "from" ? to : from;
    if (opposite === asset) return;
    if (current === asset) {
      side === "from" ? setFrom(null) : setTo(null);
      return;
    }
    side === "from" ? setFrom(asset) : setTo(asset);
  };

  const renderPills = (side: "from" | "to") => {
    const selected = side === "from" ? from : to;
    const opposite = side === "from" ? to : from;
    return (
      <Box sx={{ display: "flex", gap: "8px" }}>
        {ASSETS.map((asset) => {
          const isSelected = asset === selected;
          const isDisabled = !!opposite && asset === opposite;
          return (
            <Box
              key={`${side}-${asset}`}
              onClick={() => !isDisabled && handlePick(side, asset)}
              sx={styles.pill(isSelected, isDisabled)}
            >
              {assetLabel(asset)}
            </Box>
          );
        })}
      </Box>
    );
  };

  const amountReadout = useMemo(() => {
    if (!numericAmount || !from) return null;
    return `${numericAmount} ${assetLabel(from)}`;
  }, [numericAmount, from]);

  const handleAmountChange = (v: string) => {
    const cleaned = v.replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    const safe = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
    setAmount(safe);
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={{ "& .MuiDialog-paper": styles.dialogPaper }}>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <DialogTitle sx={styles.title}>Swap</DialogTitle>
      <Typography sx={styles.subTitle}>Select the amount, from and to</Typography>
      <DialogContent sx={styles.content}>
        <Box sx={styles.amountRow}>
          <Typography sx={styles.amountLabel}>Amount</Typography>
          <TextField
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder="0.00"
            inputMode="decimal"
            sx={styles.amountInput}
          />
          <Box sx={{ flex: 1 }} />
          {amountReadout && <Typography sx={styles.amountReadout}>{amountReadout}</Typography>}
        </Box>
        <Box sx={styles.pickersRow}>
          <Box sx={styles.pickerCol}>
            <Typography sx={styles.pickerLabel}>From</Typography>
            {renderPills("from")}
          </Box>
          <IconButton aria-label="Swap" onClick={swapSides} sx={styles.swapBtn}>
            <SwapIcon />
          </IconButton>
          <Box sx={styles.pickerCol}>
            <Typography sx={styles.pickerLabel}>To</Typography>
            {renderPills("to")}
          </Box>
        </Box>
      </DialogContent>
      {swapError && (
        <Typography sx={{ mt: "12px", color: "#B91C1C", textAlign: "center", fontSize: "13px" }}>
          Failed to swap. Please try again.
        </Typography>
      )}
      <DialogActions sx={styles.actions}>
        <Box sx={styles.actionsRow}>
          <Button onClick={handleClose} variant="outlined" sx={styles.btnRounded}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={styles.btnRounded}
            disabled={confirmDisabled || swapping}
          >
            {swapping ? "Processing..." : "Confirm"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
